import { SchedulerV3 } from '@frontend/api-schedule-v3';
import { ActionBar } from '@frontend/action-bar';
import { useAiScheduleAccess } from '@frontend/ai-receptionist-settings';
import { BulkReviewInvitationsComponents } from '@frontend/bulk-review-invitations';
import { useTranslation } from '@frontend/i18n';
import { useMessagingFeaturesEnabled } from '@frontend/messaging-enabled-hook';
import { usePaymentsModalButton, PaymentRequestModal } from '@frontend/payments-collection-flow';
import { REQUEST_ORIGINS } from '@frontend/person-invoice-create';
import { useCalendarEventsFormTypeShallowStore } from '@frontend/schedule-helpers';
import { NOT_SUPPORTED_IN_SCOPE_MESSAGE, useScopePolicy } from '@frontend/scope-access';
import { useLocation, useNavigate } from '@frontend/weave-router';
import { theme } from '@frontend/theme';
import { useModalControl, usePortal } from '@frontend/design-system';

type ActionBarProps = {
  children: React.ReactNode;
  isMobileOpen?: boolean;
  setIsMobileOpen?: (isOpen: boolean) => void;
};

export const WeaveActionBar = ({ children, isMobileOpen, setIsMobileOpen }: ActionBarProps) => {
  const { t } = useTranslation('base');
  const navigate = useNavigate();
  const { canAccessEntryPointSurface } = useScopePolicy();

  /**
   * Rendering the action bar in a portal is a workaround until we decouple the TopBar from multiple intertwined Providers
   * The current issue is that the ChatProvider is quite mixed with the TopBar which makes it hard to decouple
   * That makes it difficult to just take out the Chat Button and put it in the ActionBar (which is rendered higher in the tree)
   */
  const { Portal } = usePortal({
    mountTarget: '#main-container',
    attributes: {
      style: `height:100%; width: auto; z-index: ${theme.zIndex.higher};`,
    },
  });

  const { pathname } = useLocation();
  const isScheduleCalendar = pathname.startsWith('/schedule/calendar');

  const { setSelectedForm } = useCalendarEventsFormTypeShallowStore('setSelectedForm');
  const canAccessMessages = canAccessEntryPointSurface('quick-action.new-message');
  const canAccessPayments = canAccessEntryPointSurface('quick-action.request-payment');
  const canAccessNewAppointment = canAccessEntryPointSurface('quick-action.new-appointment');
  const canAccessNewOutOfOffice = canAccessEntryPointSurface('quick-action.new-out-of-office');
  const canAccessFax = canAccessEntryPointSurface('quick-action.new-fax');
  const canAccessReviews = canAccessEntryPointSurface('quick-action.new-review-request');

  const handleScheduleCalendarAction = (event: SchedulerV3.Types.CalendarEventsEnums) => {
    setSelectedForm(event);
  };

  const { hasWSM } = SchedulerV3.Helpers.useCheckHasWSM();
  const { canViewBridgelineScheduleSettings } = useAiScheduleAccess();

  const { paymentRequestModalProps, triggerProps, hidePaymentRequestModal } = usePaymentsModalButton();
  const modalControls = useModalControl();

  const messagingEnabled = useMessagingFeaturesEnabled();

  return (
    <Portal>
      <ActionBar
        showPopupButton={false}
        getMobileBreakpoint={(bp) => bp.xsmall.max}
        isMobileOpen={isMobileOpen}
        setIsMobileOpen={setIsMobileOpen}
        actions={[
          {
            label: t('New Message'),
            onClick: () => navigate({ to: '/messages/$filter/new', params: { filter: 'inbox' } }),
            hide: !messagingEnabled,
            disabled: !canAccessMessages,
            hoverLabel: !canAccessMessages ? NOT_SUPPORTED_IN_SCOPE_MESSAGE : undefined,
            trackingId: 'quick-action-bar-new-message-menu-item',
          },
          !hidePaymentRequestModal && {
            label: t('Request Payment'),
            onClick: () => triggerProps.onClick(),
            disabled: !canAccessPayments,
            hoverLabel: !canAccessPayments ? NOT_SUPPORTED_IN_SCOPE_MESSAGE : undefined,
            trackingId: 'quick-action-bar-request-payment-menu-item',
          },
          {
            label: t('New Appointment'),
            onClick: () => handleScheduleCalendarAction(SchedulerV3.Types.CalendarEventsEnums.APPOINTMENT_EVENT),
            disabled: !canAccessNewAppointment,
            hoverLabel: !canAccessNewAppointment ? NOT_SUPPORTED_IN_SCOPE_MESSAGE : undefined,
            hide: !hasWSM,
            trackingId: 'quick-action-bar-new-appointment-menu-item',
          },
          {
            label: t('New Out of Office Event'),
            onClick: () =>
              handleScheduleCalendarAction(SchedulerV3.Types.CalendarEventsEnums.OFFICE_HOURS_OUT_OF_OFFICE_EVENT),
            disabled: !canAccessNewOutOfOffice,
            hoverLabel: !canAccessNewOutOfOffice ? NOT_SUPPORTED_IN_SCOPE_MESSAGE : undefined,
            hide: !isScheduleCalendar || canViewBridgelineScheduleSettings,
            trackingId: 'quick-action-bar-new-out-of-office-event-menu-item',
          },
          {
            label: t('New Fax'),
            onClick: () => navigate({ to: '/fax/drafts/new' }),
            disabled: !canAccessFax,
            hoverLabel: !canAccessFax ? NOT_SUPPORTED_IN_SCOPE_MESSAGE : undefined,
            trackingId: 'quick-action-bar-new-fax-menu-item',
          },
          {
            label: t('New Review Request'),
            onClick: () => modalControls.openModal(),
            disabled: !canAccessReviews,
            hoverLabel: !canAccessReviews ? NOT_SUPPORTED_IN_SCOPE_MESSAGE : undefined,
          },
        ].filter(Boolean)}
      >
        {children}
      </ActionBar>
      <PaymentRequestModal {...paymentRequestModalProps} origin={REQUEST_ORIGINS.QUICK_ACTION_SIDEBAR} />
      <BulkReviewInvitationsComponents.BulkReviewInvitationsModal {...modalControls.modalProps} />
    </Portal>
  );
};
